import * as React from "react";
import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { FeatureFlags, defaultFeatureFlags } from "../config/featureFlags";
import { getUserFeatures } from "../services/user-features.service";
import { useWorkflowIntelligence } from "./WorkflowIntelligenceContext";

const FeatureFlagContext = createContext<FeatureFlags | undefined>(undefined);

interface FeatureFlagProviderProps {
  children: ReactNode;
}

/**
 * Per-user feature flag loader. Waits for auth to settle (via
 * WorkflowIntelligenceContext.userInfo), then queries user features and
 * exposes them as a flag map. Falls back to defaults on failure.
 */
export const FeatureFlagProvider: React.FC<FeatureFlagProviderProps> = ({ children }) => {
  const [flags, setFlags] = useState<FeatureFlags>(defaultFeatureFlags);
  // Wait for MSAL auth to be established (userInfo populated by WorkflowIntelligenceContext)
  // before calling the API, otherwise the first-init auth race causes a silent failure.
  const { userInfo } = useWorkflowIntelligence();

  useEffect(() => {
    if (!userInfo) return;
    getUserFeatures()
      .then((features) => {
        // Only grant-backed flags are resolved from the API. Generally
        // available features are left at their `true` default and
        // deliberately NOT listed here — spreading defaults and then
        // overwriting every key is what made "just flip the default to
        // true" silently not work. To put a GA feature back behind a
        // grant, flip its default to false in featureFlags.ts and add it
        // back to this object.
        const nextFlags: FeatureFlags = {
          ...defaultFeatureFlags,
          enableAI: features.includes("AI"),
        };
        setFlags(nextFlags);
      })
      .catch((err) => {
        console.error("Failed to load user features, falling back to defaults:", err);
      });
  }, [userInfo]);

  return <FeatureFlagContext.Provider value={flags}>{children}</FeatureFlagContext.Provider>;
};

export const useFeatureFlags = (): FeatureFlags => {
  const context = useContext(FeatureFlagContext);
  if (!context) {
    throw new Error("useFeatureFlags must be used within a FeatureFlagProvider");
  }
  return context;
};
